/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/layout/AdminLayout';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import {
  RefreshCw, AlertTriangle, Loader2, CheckCircle2, ClipboardCheck, Package,
  Beaker,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const STATUS_TONE = {
  ok: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  low: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  exhausted: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  mismatch: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
  unverified: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
};

export default function FormulationInventory() {
  const { subscriberId, phaseId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canVerify = user?.role === 'admin' || user?.role === 'counselor';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [verifyRow, setVerifyRow] = useState(null); // { row }
  const [verifying, setVerifying] = useState(false);
  const [verifyForm, setVerifyForm] = useState({
    physical_remaining_count: '', mismatch_reason: '', subscriber_explanation: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(
        `${API}/formulation/inventory/${subscriberId}/${phaseId}`,
      );
      setData(r.data);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load inventory');
    } finally {
      setLoading(false);
    }
  }, [subscriberId, phaseId]);
  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(
        `${API}/formulation/inventory/${subscriberId}/${phaseId}/sync`,
      );
      toast.success(`Synced ${r.data?.synced_count ?? 0} items`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openVerify = (row) => {
    setVerifyRow(row);
    setVerifyForm({
      physical_remaining_count: row.physical_remaining_count ?? '',
      mismatch_reason: row.mismatch_reason ?? '',
      subscriber_explanation: row.subscriber_explanation ?? '',
    });
  };

  const submitVerify = async () => {
    if (!verifyRow) return;
    if (verifyForm.physical_remaining_count === '' || isNaN(Number(verifyForm.physical_remaining_count))) {
      toast.error('Physical remaining count must be a number');
      return;
    }
    setVerifying(true);
    try {
      await axios.patch(
        `${API}/formulation/inventory/${verifyRow.id}/physical-verify`,
        {
          physical_remaining_count: Number(verifyForm.physical_remaining_count),
          mismatch_reason: verifyForm.mismatch_reason || null,
          subscriber_explanation: verifyForm.subscriber_explanation || null,
        },
      );
      toast.success('Physical verification recorded');
      setVerifyRow(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  if (!user) return null;

  return (
    <AdminLayout>
      <div
        data-testid="formulation-inventory-page"
        className="px-6 py-6 max-w-6xl mx-auto"
      >
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1">
            <Beaker className="w-6 h-6 text-cyan-300" />
            <h1 className="text-2xl font-semibold text-white">Phase Inventory</h1>
            <Badge
              data-testid="formulation-inventory-f3-badge"
              className="bg-cyan-500/15 text-cyan-200 border-cyan-500/30 text-[10px]"
            >
              F3
            </Badge>
          </div>
          <p className="text-sm text-white/50">
            Inventory is computed from the subscriber&rsquo;s executable protocol and execution logs.
            Counselors enter physical counts to confirm system estimates.
          </p>
          <p className="text-xs text-white/40 mt-1">
            <span className="font-mono">subscriber:</span> {subscriberId}
            {' · '}
            <span className="font-mono">phase:</span> {phaseId}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          {isAdmin && (
            <Button
              data-testid="inventory-sync-btn"
              onClick={runSync}
              disabled={syncing}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30 h-9 text-xs"
            >
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Sync from execution logs
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            className="border-white/15 text-white/70 h-9 text-xs"
            data-testid="inventory-refresh-btn"
          >
            Refresh
          </Button>
        </div>

        {loading && (
          <div className="rounded border border-white/10 bg-slate-900/40 p-8 text-center text-white/60">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
            Loading…
          </div>
        )}

        {pageError && !loading && (
          <div
            data-testid="inventory-page-error"
            className="rounded border border-amber-500/30 bg-amber-500/5 p-6 text-center"
          >
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-300 mb-2" />
            <div className="text-sm text-white/80">{pageError}</div>
            <Button size="sm" variant="outline" className="mt-3 border-white/15" onClick={load}>
              Retry
            </Button>
          </div>
        )}

        {data && <StatsRollup stats={data.stats} />}

        {data && data.items.length === 0 && !loading && (
          <div
            data-testid="inventory-empty-state"
            className="rounded-lg border border-white/10 bg-slate-900/40 p-8 text-center"
          >
            <Package className="w-6 h-6 mx-auto text-white/30 mb-2" />
            <div className="text-sm text-white/70">No inventory rows yet.</div>
            <div className="text-xs text-white/50 mt-1">
              Run <span className="text-cyan-300">Sync from execution logs</span> to populate inventory.
            </div>
          </div>
        )}

        {data && data.items.length > 0 && (
          <InventoryTable
            items={data.items}
            canVerify={canVerify}
            onVerify={openVerify}
          />
        )}
      </div>

      {/* Verify modal */}
      <Dialog open={!!verifyRow} onOpenChange={(open) => !open && setVerifyRow(null)}>
        <DialogContent
          data-testid="inventory-verify-modal"
          className="bg-slate-900 border border-white/10"
        >
          <DialogHeader>
            <DialogTitle className="text-white">Enter physical verification</DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              Count the physical bottles / doses remaining for this item. The server will
              compute any mismatch against the system estimate.
            </DialogDescription>
          </DialogHeader>

          {verifyRow && (
            <div className="space-y-3 py-2">
              <div className="rounded border border-white/10 bg-slate-950/40 p-2 text-xs text-white/70">
                <div className="font-medium text-white">{verifyRow.item_name}</div>
                <div className="font-mono text-[10px] text-cyan-200">
                  {verifyRow.raw_base_code || '—'}
                </div>
                <div className="mt-1">
                  System estimate remaining:{' '}
                  <span className="text-white">{verifyRow.system_estimated_remaining}</span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-white/60">Physical remaining count *</Label>
                <Input
                  data-testid="inventory-physical-count-input"
                  type="number"
                  min={0}
                  value={verifyForm.physical_remaining_count}
                  onChange={(e) =>
                    setVerifyForm((f) => ({ ...f, physical_remaining_count: e.target.value }))
                  }
                  className="bg-slate-950/40 border-white/10 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-white/60">Mismatch reason (optional)</Label>
                <Input
                  data-testid="inventory-mismatch-reason-input"
                  maxLength={500}
                  value={verifyForm.mismatch_reason}
                  onChange={(e) =>
                    setVerifyForm((f) => ({ ...f, mismatch_reason: e.target.value }))
                  }
                  placeholder="e.g., subscriber took doses while travelling without logging"
                  className="bg-slate-950/40 border-white/10 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-white/60">Subscriber explanation (optional)</Label>
                <Textarea
                  data-testid="inventory-subscriber-explanation-input"
                  maxLength={1000}
                  rows={3}
                  value={verifyForm.subscriber_explanation}
                  onChange={(e) =>
                    setVerifyForm((f) => ({ ...f, subscriber_explanation: e.target.value }))
                  }
                  placeholder="Subscriber&rsquo;s own account of what happened…"
                  className="bg-slate-950/40 border-white/10 text-sm"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVerifyRow(null)}
              className="border-white/15 text-white/80"
            >
              Cancel
            </Button>
            <Button
              data-testid="inventory-verify-confirm-btn"
              onClick={submitVerify}
              disabled={verifying}
              className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/30 hover:bg-cyan-500/30"
            >
              {verifying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <ClipboardCheck className="w-3.5 h-3.5 mr-1" />}
              Record verification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function StatsRollup({ stats }) {
  const chips = [
    ['Items', stats.total_items, 'text-white'],
    ['Verified', stats.verified, 'text-emerald-300'],
    ['Unverified', stats.unverified, 'text-slate-300'],
    ['Mismatch', stats.mismatch, 'text-fuchsia-300'],
    ['Low', stats.low, 'text-amber-300'],
    ['Exhausted', stats.exhausted, 'text-rose-300'],
    ['OK', stats.ok, 'text-emerald-300'],
  ];
  return (
    <div
      data-testid="inventory-stats-rollup"
      className="rounded-lg border border-white/10 bg-white/[0.02] p-3 mb-3 flex flex-wrap gap-4 items-center text-xs"
    >
      {chips.map(([label, value, tone]) => (
        <div key={label} className="flex items-center gap-2">
          <span className="text-white/40">{label}:</span>
          <span className={`font-semibold ${tone}`}>{value}</span>
        </div>
      ))}
    </div>
  );
}

function InventoryTable({ items, canVerify, onVerify }) {
  return (
    <div
      data-testid="inventory-table"
      className="rounded-lg border border-white/10 bg-slate-900/40 overflow-hidden"
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-950/60 border-b border-white/10 text-white/50">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Item</th>
              <th className="text-left px-3 py-2 font-medium">Code</th>
              <th className="text-right px-3 py-2 font-medium">Planned</th>
              <th className="text-right px-3 py-2 font-medium">Executed</th>
              <th className="text-right px-3 py-2 font-medium">Skipped</th>
              <th className="text-right px-3 py-2 font-medium">Paused</th>
              <th className="text-right px-3 py-2 font-medium">Est. remaining</th>
              <th className="text-right px-3 py-2 font-medium">Adherence %</th>
              <th className="text-center px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Phys. count</th>
              <th className="text-right px-3 py-2 font-medium">Mismatch</th>
              <th className="text-center px-3 py-2 font-medium">Verified</th>
              <th className="text-right px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id}
                data-testid={`inventory-row-${row.id}`}
                className="border-b border-white/5"
              >
                <td className="px-3 py-2 text-white/80">{row.item_name}</td>
                <td className="px-3 py-2 font-mono text-cyan-200">{row.raw_base_code || '—'}</td>
                <td className="px-3 py-2 text-right text-white/70">{row.planned_quantity}</td>
                <td className="px-3 py-2 text-right text-emerald-300">{row.executed_quantity}</td>
                <td className="px-3 py-2 text-right text-amber-300">{row.skipped_quantity}</td>
                <td className="px-3 py-2 text-right text-slate-300">{row.paused_quantity}</td>
                <td className="px-3 py-2 text-right text-white/80">{row.system_estimated_remaining}</td>
                <td className="px-3 py-2 text-right text-white/70">{row.adherence_percentage}%</td>
                <td className="px-3 py-2 text-center">
                  <span
                    data-testid={`inventory-status-${row.id}`}
                    className={`px-2 py-0.5 rounded border text-[10px] ${STATUS_TONE[row.inventory_status] || ''}`}
                  >
                    {row.inventory_status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-white/60">
                  {row.physical_remaining_count ?? '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  {row.mismatch_quantity == null
                    ? <span className="text-white/30">—</span>
                    : <span className={row.mismatch_quantity === 0 ? 'text-emerald-300' : 'text-fuchsia-300'}>
                        {row.mismatch_quantity > 0 ? '+' : ''}{row.mismatch_quantity}
                      </span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {row.physical_verified ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 inline" />
                  ) : (
                    <span className="text-white/30 text-[10px]">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {canVerify && (
                    <Button
                      data-testid={`inventory-verify-btn-${row.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => onVerify(row)}
                      className="border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/10 h-7 text-[11px]"
                    >
                      Verify physical
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
